import { SocketClient } from "cbcore-ts"
import { NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { CompanyViewController } from "../CompanyView/CompanyViewController"
import { RHCore } from "../Core/RHCore"
import { RHProcurementAmendmentPresentation } from "../Core/RHProcurementAmendmentPresentation"
import { RHProcurementDeadlineAutoExtensionPresentation } from "../Core/RHProcurementDeadlineAutoExtensionPresentation"
import { CBColumnDescriptor, CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterCriteria, RHFilterPanelView } from "../Custom components/RH/RHFilterPanelView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { formatRHDateTime, formatRHDateTimeWithSecondsValue } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHColors, RHFont, RHSpacing, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import { RHEventLogViewTokens, RHLogEventViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import { AuditLogEntry, RHProcurementPrivateMessageSenderRole } from "../SocketClientFunctions"
import { configureEventLogActionCell } from "./EventLogTableCellStyling"
import { LogViewController } from "./LogViewController"
import { NotificationDetailsView } from "./NotificationDetailsView"

// ─────────────────────────────────────────────────────────────────────────────
// Flat row type shown in the data view
// ─────────────────────────────────────────────────────────────────────────────

type ProcurementTab = "details" | "team" | "makeOffer" | "offers" | "qa" | "contracts"

type NotificationInboxTarget = {
    procurementTab: ProcurementTab
    qaSubtab?: "private"
}

interface NotificationRow {
    _id: string
    eventType: string
    contentId?: string
    /** Sortable ISO timestamp — the data view sorts the "Aeg" column by this raw
     *  field value as a string, so it must NOT be the localized display format
     *  (DD.MM.YYYY compares by day-of-month first and buries the newest rows). */
    timestamp: string
    /** Localized display text for the "Aeg" cell. */
    timestampText: string
    direction?: "incoming" | "outgoing"
    eventDescription: string
    procurementId: string
    procurementTab?: ProcurementTab
    qaSubtab?: "private"
    privateMessageCompanyId?: string
    routeCompanyId?: string
    routeApplicationMode?: "procurer" | "offerer"
    questionId?: string
    companyTab?: "administration" | "affiliation"
    companyId?: string
    workflowId?: string
    /** True while the recipient has not acknowledged the event anywhere — drives
     *  the bold "new" row styling. Related content, a row click, or
     *  "Märgi kõik nähtuks" clears it; opening this page alone marks nothing. */
    isNew: boolean
    /** Which of the recipient's own companies this row concerns — see AuditLogEntry.notificationCompany. */
    companyName?: string
}
// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function eventTypeLabel(eventType: string): string {
    const label = RHLogEventViewTokens.eventTypeLabels[eventType]
    if (label) {
        return label
    }
    return "Tundmatu sündmus"
}

function procurementTabForEventType(eventType: string): ProcurementTab | undefined {
    const notificationInboxTarget = NotificationsViewController._inboxTargetForEventType(eventType)
    if (notificationInboxTarget) {
        return notificationInboxTarget.procurementTab
    }

    const incomingOfferTab = NotificationsViewController
        ._incomingOfferProcurementTab(eventType)
    if (incomingOfferTab) {
        return incomingOfferTab
    }

    const contractTab = NotificationsViewController._contractProcurementTab(eventType)
    if (contractTab) {
        return contractTab
    }

    const amendmentTab = NotificationsViewController._amendmentProcurementTab(eventType)
    if (amendmentTab) {
        return amendmentTab
    }

    const procurementTeamTab = NotificationsViewController
        ._procurementTeamTabForEventType(eventType)
    if (procurementTeamTab) {
        return procurementTeamTab
    }

    switch (eventType) {
        case "procurement.published":
        case "procurement.deadline_passed":
        case "procurement.deadline_changed":
        case "procurement.deadline_auto_extended":
        case "procurement.tenderer_registered":
        case "procurement.closed_without_contract":
            return "details"
        case "offer.rescinded":
        case "offer.part_rescinded":
            return "offers"
        case "offer.opened":
        case "offer.notification_sent":
            return "makeOffer"
        case "question.asked":
        case "question.answered":
        case "procurerBroadcast.sent":
            return "qa"
        default:
            return undefined
    }
}

function textField(data: Record<string, unknown>, key: string): string | undefined {
    const value = data[key]
    return typeof value === "string" ? value : undefined
}

function numberField(data: Record<string, unknown>, key: string): number | undefined {
    const value = data[key]
    return typeof value === "number" ? value : undefined
}

function booleanField(data: Record<string, unknown>, key: string): boolean | undefined {
    const value = data[key]
    return typeof value === "boolean" ? value : undefined
}

function dateField(data: Record<string, unknown>, key: string): Date | undefined {
    const value = data[key]
    if (typeof value === "string" || typeof value === "number") {
        return new Date(value)
    }
    return undefined
}

function buildNotificationDescription(entry: AuditLogEntry): string {
    if (entry.eventType === "communication.email_delivered" || entry.eventType === "communication.email_delivery_failed") {
        let subject = "Teatis"
        if (typeof entry.eventData.subject === "string" && entry.eventData.subject.trim()) subject = entry.eventData.subject
        return RHTextSanitizer.escapedHTML(subject)
    }
    const data = entry.eventData
    const partNumber = numberField(data, "partNumber")
    const partCount = numberField(data, "partCount")
    let procurementTitle: string | undefined
    if (typeof data.procurementTitle === "string" && data.procurementTitle.trim()) {
        procurementTitle = data.procurementTitle.trim()
    }
    let procurementName = ""
    if (procurementTitle) {
        procurementName = ` „${RHTextSanitizer.escapedHTML(procurementTitle)}“`
    }
    let membershipCompanyName: string | undefined
    if (typeof data.companyName === "string" && data.companyName.trim()) {
        membershipCompanyName = data.companyName.trim()
    }
    const membershipDescription =
        NotificationsViewController._companyMembershipDescriptionHTML(
            entry.eventType,
            membershipCompanyName,
            data
        )
    if (membershipDescription) {
        return membershipDescription
    }

    const incomingOfferDescription = NotificationsViewController
        ._incomingOfferDescriptionHTML(entry.eventType, procurementTitle, partNumber, textField(data, "companyName"))
    if (incomingOfferDescription) {
        return incomingOfferDescription
    }

    const procurementTeamDescription = NotificationsViewController
        ._procurementTeamDescriptionHTML(entry.eventType, procurementTitle, textField(data, "role"))
    if (procurementTeamDescription) {
        return procurementTeamDescription
    }

    const notificationInboxDescription = NotificationsViewController
        ._inboxDescriptionHTMLForEntry(entry)
    if (notificationInboxDescription) {
        return notificationInboxDescription
    }
    
    switch (entry.eventType) {
        // Procurer notifications
        case "offer.rescinded":
            if (procurementTitle) {
                return `Pakkumus võeti tagasi hankes${procurementName}`
            }
            return "Pakkumus võeti tagasi"
        case "offer.part_rescinded":
            if (partNumber !== undefined) {
                return `Hanke${procurementName} osa ${partNumber} pakkumus võeti tagasi`
            }
            return `Hanke${procurementName} osa pakkumus võeti tagasi`
        case "offer.opened":
            if (procurementTitle) {
                return `Hankija avas pakkumused hankes${procurementName}`
            }
            return "Hankija avas pakkumused"
        case "question.asked":
            if (procurementTitle) {
                return `Esitati küsimus hankes${procurementName}`
            }
            return "Esitati küsimus"
        case "procurement.deadline_passed":
            return `Hanke${procurementName} pakkumuste tähtaeg on möödunud`
        case "procurement.published":
            return `Avaldati hange${procurementName}`
        case "procurement.closed_without_contract": {
            let description = `Hange${procurementName} lõpetati ilma lepinguta`
            if (data.isReconciliationRound === YES) {
                description = `Täpsustav voor${procurementName} lõpetati ilma lepinguta`
            }
            if (typeof data.reason === "string" && data.reason.trim()) {
                description += `. Põhjus: ${RHTextSanitizer.escapedHTML(data.reason)}`
            }
            return description
        }
        case "procurement.amended":
            return RHProcurementAmendmentPresentation.descriptionHTML(procurementTitle)
        
        // Offerer notifications
        case "offer.evaluation_reopened": {
            let partDescription = ""
            if (partCount !== undefined && partCount > 1 && partNumber !== undefined) {
                partDescription = ` osa ${partNumber}`
            }
            return `Hanke${procurementName}${partDescription} varasem otsus muudeti ja hindamine jätkub. Põhjendus: ` +
                RHTextSanitizer.escapedHTML(String(data.reason ?? ""))
        }
        case "offer.notification_sent": {
            if (data.wasExcludedFromNextRound === YES) {
                let partDescription = ""
                if (partCount !== undefined && partCount > 1 && partNumber !== undefined) {
                    partDescription = ` osa ${partNumber}`
                }
                return `Sinu ettevõtet ei kutsutud hanke${procurementName}${partDescription} järgmisse vooru`
            }
            let formattedOfferPrice: string | undefined
            const offerPrice = data.offerPrice as Record<string, unknown> | undefined
            if (
                offerPrice &&
                typeof offerPrice.amount === "number" &&
                Number.isFinite(offerPrice.amount)
            ) {
                let currency = "EUR"
                if (
                    typeof offerPrice.currency === "string" &&
                    /^[A-Za-z]{3}$/.test(offerPrice.currency.trim())
                ) {
                    currency = offerPrice.currency.trim().toUpperCase()
                }
                formattedOfferPrice = RHEuroFormatter.formattedCurrency(
                    offerPrice.amount,
                    {
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }
                )
            }
            return NotificationsViewController._offerResultDescriptionHTML(
                procurementTitle,
                data.isWinner === YES,
                formattedOfferPrice,
                partNumber,
                partCount,
                booleanField(data, "partHasWinner"),
                booleanField(data, "isReconciliationRound")
            )
        }
        case "question.answered":
            return NotificationsViewController._answeredQuestionDescriptionHTML(
                procurementTitle,
                data.isQuestionFromRecipientCompany === YES
            )
        case "procurerBroadcast.sent":
            if (procurementTitle) {
                return `Hankija saatis sõnumi hankes${procurementName}`
            }
            return "Hankija saatis sõnumi"
        case "procurement.deadline_changed": {
            const changedDeadline = dateField(data, "newDeadline")
            const newDeadline = changedDeadline ? formatRHDateTime(changedDeadline) : undefined
            return newDeadline
                   ? `Hanke${procurementName} tähtaega muudeti — uus tähtaeg: ${newDeadline}`
                   : `Hanke${procurementName} tähtaega muudeti`
        }
        case "procurement.deadline_auto_extended": {
            let newDeadline: string | undefined
            const extendedDeadline = dateField(data, "newDeadline")
            if (extendedDeadline) {
                newDeadline = formatRHDateTime(extendedDeadline)
            }
            if (NotificationsViewController._inboxApplicationModeForEntry(entry) === "offerer") {
                return RHProcurementDeadlineAutoExtensionPresentation
                    .offererDescriptionHTMLWithProcurementTitleAndNewDeadlineText(procurementTitle, newDeadline)
            }
            if (newDeadline) {
                return `Hanke${procurementName} tähtaeg pikenes automaatselt. Uus tähtaeg: ${newDeadline}`
            }
            return `Hanke${procurementName} tähtaeg pikenes automaatselt`
        }
        case "contract.signed":
            return NotificationsViewController._signedContractDescriptionHTML(
                procurementTitle,
                partNumber,
                partCount
            )
        case "contract.cancelled":
            return NotificationsViewController._cancelledContractDescriptionHTML(
                procurementTitle,
                partNumber,
                partCount,
                data.wasSignedBeforeCancellation === YES,
                textField(data, "nonConclusionReason")
            )
        
        default:
            return eventTypeLabel(entry.eventType)
    }
}

function flattenEntry(entry: AuditLogEntry): NotificationRow {
    const procurementId =
        (entry.target.type === "procurement" ? entry.target.id : "") ||
        (entry.eventData?.procurementId as string) || ""
    
    const questionId =
        (entry.eventType === "question.asked" || entry.eventType === "question.answered")
        ? (entry.eventData?.questionId as string) || ""
        : ""
    
    const companyTab = NotificationsViewController._companyMembershipTabForEvent(
        entry.eventType,
        entry.eventData
    )
    const companyId = entry.notificationCompany?._id ??
        (entry.eventData?.companyId as string | undefined)
    let companyName = entry.notificationCompany?.name?.trim() || undefined
    if (entry.eventType.startsWith("company.")) {
        // Membership events name their company explicitly, including invitations outside the active company scope.
        companyName = undefined
        if (typeof entry.eventData.companyName === "string") {
            companyName = entry.eventData.companyName.trim() || undefined
        }
    }
    const notificationInboxTarget = NotificationsViewController._inboxTargetForEventType(
        entry.eventType
    )
    let privateMessageCompanyId: string | undefined
    if (entry.eventType === "privateMessage.sent") {
        const offererCompanyId = entry.eventData?.offererCompanyId
        if (typeof offererCompanyId === "string") {
            privateMessageCompanyId = offererCompanyId
        }
    }
    const routeCompanyId = NotificationsViewController._inboxRecipientCompanyIdForEntry(entry)
    const routeApplicationMode = entry.notificationApplicationMode ?? NotificationsViewController._inboxApplicationModeForEntry(entry)
    let procurementTab: ProcurementTab | undefined
    if (procurementId) {
        procurementTab = procurementTabForEventType(entry.eventType)
        if (entry.eventType === "offer.evaluation_reopened") {
            procurementTab = "makeOffer"
            if (routeApplicationMode === "procurer") {
                procurementTab = "offers"
            }
        }
        if (entry.notificationApplicationMode === "procurer") {
            if (procurementTab === "makeOffer") procurementTab = "offers"
            if (!procurementTab) procurementTab = "details"
        }
    }
    
    return {
        _id: entry._id,
        eventType: entry.eventType,
        contentId: textField(entry.eventData, "messageId") ?? textField(entry.eventData, "questionId") ??
            textField(entry.eventData, "threadId") ?? textField(entry.eventData, "amendmentId") ?? textField(entry.eventData, "offerId"),
        timestamp: RHTimestampSorting.sortableISOString(entry.timestamp),
        timestampText: formatRHDateTimeWithSecondsValue(entry.timestamp),
        direction: entry.notificationDirection,
        eventDescription: buildNotificationDescription(entry),
        procurementId,
        procurementTab,
        qaSubtab: notificationInboxTarget?.qaSubtab,
        privateMessageCompanyId,
        routeCompanyId,
        routeApplicationMode,
        questionId: questionId || undefined,
        companyTab,
        companyId,
        workflowId: entry.eventData?.requestId as string | undefined,
        isNew: entry.isNew ?? NO,
        companyName
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// View Controller
// ─────────────────────────────────────────────────────────────────────────────

export class NotificationsViewController extends UIViewController {
    
    static override readonly routeComponentName = "notifications"
    static override readonly ParameterIdentifierName = {}
    readonly titleKey = "notifications"
    
    // ── Subviews ──────────────────────────────────────────────────────────────
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        // UITextView.type.header2
        UITextView.type.header4
    ).configuredWithObject({
        text: "Minu teatised",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    readonly unseenBadgeLabel = new UITextView(
        this.view.elementID + "UnseenBadge"
    ).configuredWithObject({
        backgroundColor: RHColor.primary,
        textColor: RHColor.textWhite,
        isSingleLine: true,
        fontSize: 12
    }).addedAsSubviewToView(this.view)

    // Right-aligned utility action on the title row — marks every notification in
    // the inbox seen and clears its unread emphasis throughout the navigation.
    // Always visible but DISABLED when there is nothing unseen to mark. Wired in
    // the constructor.
    readonly markAllSeenButton = new RHButton(this.view.elementID + "MarkAllSeenButton").configuredWithObject({
        variant: "primary",
        contentPadding: RHSpacing.medium,
        titleLabel: {
            text: "Märgi kõik nähtuks",
            style: { fontSize: RHFont.sizeSmall }
        }
    }).addedAsSubviewToView(this.view)

    readonly filterAllCompaniesButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Kõik minu ettevõtted" },
        enabled: NO
    })
    readonly filterAllButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Kõik teatised" }, chipActive: YES
    })
    readonly filterIncomingButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Sissetulnud" }
    })
    readonly filterOutgoingButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Välja läinud" }
    })
    readonly moreFiltersButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Muud filtrid ▾" }
    })
    readonly filterChipBar = new RHFilterChipBarView().addedAsSubviewToView(this.view)
    readonly filterPanel = new RHFilterPanelView().configuredWithObject({
        usesExternalToggle: YES,
        fields: [
            { key: "referenceNumber", label: "Hanke viitenumber", kind: "text" },
            { key: "title", label: "Hanke nimetus", kind: "text" },
            { key: "recipient", label: "Teate saaja", kind: "text" }
        ]
    })
    readonly previousPageButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Eelmine" }, enabled: NO
    }).addedAsSubviewToView(this.view)
    readonly nextPageButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Järgmine" }, enabled: NO
    }).addedAsSubviewToView(this.view)
    readonly pageLabel = new UITextView().addedAsSubviewToView(this.view)
    readonly loadFailureLabel = new UITextView().configuredWithObject({ isSingleLine: NO })
    readonly detailsView = new NotificationDetailsView()
    detailsVisibilityDidChange?: (isShowingDetails: boolean) => void
    _selectedNotification?: NotificationRow
    _detailsLoadGeneration = 0
    _direction?: "incoming" | "outgoing"
    _filterCriteria: RHFilterCriteria = {}
    _pageNumber = 1
    _notificationCount = 0
    _isAllCompaniesScopeActive = NO
    // Applies the top bar's default scope once, then leaves later local toggling alone.
    _hasAppliedDefaultCompanyScope = NO

    // See MyOngoingProcurementsViewController._showsCompanyColumn for the same pattern.
    _showsCompanyColumn = NO

    readonly dataView = new RHDataView<NotificationRow>().configuredWithObject({
            noDataMessage: "Teated puuduvad",
            searchTextField: { placeholderText: "Otsi sellelt leheküljelt" },
            descriptors: this._buildDescriptors()
        })
        .addedAsSubviewToView(this.view)

    // Rebuildable so _updateCompanyColumnVisibility can toggle the "Ettevõte" column.
    _buildDescriptors(): CBColumnDescriptor<NotificationRow>[] {
        const companyDescriptor: CBColumnDescriptor<NotificationRow>[] = []
        if (this._showsCompanyColumn) {
            companyDescriptor.push(CBDataViewCellDescriptorWithObject<NotificationRow>({
                keyPath: "companyName",
                title: "Ettevõte",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    configureEventLogActionCell(
                        cellView,
                        this._boldIfUnseen(RHTextSanitizer.escapedHTML(dataItem.companyName ?? "-"), dataItem.isNew),
                        this._rowHoverTextForNotification(dataItem)
                    )
                },
                buttonWasPressed: (_cellView, dataItem) => this._didActivateRow(dataItem)
            }))
        }
        return [
                CBDataViewCellDescriptorWithObject<NotificationRow>({
                    keyPath: "timestamp",
                    title: "Aeg",
                    allowSorting: true,
                    dataType: "string",
                    initialOrderingState: "descending",
                    titleLabelAlignment: UITextView.textAlignment.left,
                    cellLabelAlignment: UITextView.textAlignment.left,
                    // Always a button so the WHOLE row is clickable. Clicking marks
                    // the row seen (link rows additionally open their target — see
                    // _didActivateRow). Keeping every cell permanently a button means
                    // the cell variant never flips, so the click wiring attached at
                    // cell creation survives row reuse.
                    updateCell: (cellView, dataItem) => {
                        let directionLabel = "Suund määramata"
                        let arrowColor = RHColors.textSecondary
                        let arrowPath = "M3 7H11"
                        if (dataItem.direction === "outgoing") {
                            directionLabel = "Välja läinud"
                            arrowColor = RHColors.dangerRed
                            arrowPath = "M3 11L11 3M4 3H11V10"
                        } else if (dataItem.direction === "incoming") {
                            directionLabel = "Sissetulnud"
                            arrowColor = RHColors.successGreenDark
                            arrowPath = "M11 3L3 11M3 4V11H10"
                        }
                        const directionMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" ' +
                            'viewBox="0 0 14 14" fill="none" role="img" aria-label="' + directionLabel +
                            '" style="color:' + arrowColor.stringValue + ';vertical-align:middle">' +
                            '<title>' + directionLabel + '</title><path d="' + arrowPath +
                            '" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> '
                        configureEventLogActionCell(
                            cellView,
                            directionMarkup + this._boldIfUnseen(dataItem.timestampText, dataItem.isNew),
                            this._rowHoverTextForNotification(dataItem)
                        )
                    },
                    buttonWasPressed: (_cellView, dataItem) => this._didActivateRow(dataItem)
                }),
                CBDataViewCellDescriptorWithObject<NotificationRow>({
                    keyPath: "eventDescription",
                    title: "Teatis",
                    allowSorting: false,
                    titleLabelAlignment: UITextView.textAlignment.left,
                    cellLabelAlignment: UITextView.textAlignment.left,
                    updateCell: (cellView, dataItem) => {
                        configureEventLogActionCell(
                            cellView,
                            this._boldIfUnseen(dataItem.eventDescription, dataItem.isNew),
                            this._rowHoverTextForNotification(dataItem)
                        )
                        cellView.configureAsWrappingMultilineText()
                    },
                    buttonWasPressed: (_cellView, dataItem) => this._didActivateRow(dataItem)
                }),
                ...companyDescriptor,
                CBDataViewCellDescriptorWithObject<NotificationRow, RHTableActionButtonView>({
                    keyPath: "", title: "", allowSorting: NO, absoluteWidth: 120,
                    initCustomView: () => new RHTableActionButtonView().configuredWithObject({
                        preferredButtonWidth: 92,
                        preferredButtonHeight: 28
                    }),
                    updateCell: (cellView, dataItem) => {
                        cellView.customView.configureWithObject({
                            text: "Detailid", buttonWasPressed: () => { void this._showDetailsForNotification(dataItem) }
                        })
                    }
                })
        ]
    }

    // ── State ─────────────────────────────────────────────────────────────────
    
    _rows: NotificationRow[] = []
    _loadGeneration = 0
    _unseenCount = 0
    readonly _pageSize = 100
    
    // ── Constructor ───────────────────────────────────────────────────────────
    
    constructor(view: UIView) {
        
        super(view)

        this.view.configureWithObject({ backgroundColor: UIColor.whiteColor })
        RHNotificationBadge.configurePageLabel(this.unseenBadgeLabel)
        this.unseenBadgeLabel.hidden = true

        // Disabled until a load reveals unseen notifications to mark.
        this.markAllSeenButton.enabled = NO
        this.markAllSeenButton.controlEventTargetAccumulator.PrimaryActionTriggered = (_sender, _event) => {
            this._didPressMarkAllSeen()
        }
        this._applyTableColumnLayout()

        // Changes company scope, so it re-fetches rather than just re-filtering.
        this.filterAllCompaniesButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._isAllCompaniesScopeActive = NO == this._isAllCompaniesScopeActive
            this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
            this._pageNumber = 1
            void this._loadNotifications()
        }

        this.filterAllButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._direction = undefined
            this.filterPanel.clearCriteria()
            this._refreshFilterChips()
        }
        for (const [button, direction] of [
            [this.filterIncomingButton, "incoming"], [this.filterOutgoingButton, "outgoing"]
        ] as const) {
            button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                if (this._direction === direction) return
                this._direction = direction
                this._pageNumber = 1
                this._refreshFilterChips()
                void this._loadNotifications()
            }
        }
        this.moreFiltersButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.filterPanel.isExpanded = !this.filterPanel.isExpanded
        }
        this.filterPanel.isExpandedDidChange = expanded => {
            this.moreFiltersButton.chipOpen = expanded
            if (expanded) this.filterPanel.addedAsSubviewToView(this.view)
            else this.filterPanel.removeFromSuperview()
            this.view.setNeedsLayout()
        }
        this.filterPanel.filterCriteriaDidChange = criteria => {
            this._filterCriteria = criteria
            this._pageNumber = 1
            this._refreshFilterChips()
            const referenceNumber = this._filterTextForKey("referenceNumber") ?? ""
            let route = UIRoute.currentRoute.routeByRemovingParameterInComponent(
                LogViewController.routeComponentName, LogViewController.ParameterIdentifierName.referenceNumber
            )
            if (referenceNumber) {
                route = route.routeBySettingParameterInComponent(
                    LogViewController.routeComponentName, LogViewController.ParameterIdentifierName.referenceNumber, referenceNumber
                )
            }
            route.applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
            void this._loadNotifications()
        }
        this.previousPageButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (this._pageNumber > 1) { this._pageNumber--; void this._loadNotifications() }
        }
        this.nextPageButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (this._pageNumber * this._pageSize < this._notificationCount) { this._pageNumber++; void this._loadNotifications() }
        }
        this.detailsView.openProcurementButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (this._selectedNotification) this._navigateToRowTargetIfAny(this._selectedNotification)
        }
        this._refreshFilterChips()

    }
    
    // ── Unseen badge ──────────────────────────────────────────────────────────
    
    private _applyTableColumnLayout() {
        const cellWeights = this._currentColumnWeights()
        this.dataView.tableHeaderView.configureWithObject({
            cellWeights
        })
        this.dataView.tableView.configureWithObject({
            cellWeights
        })
        this.dataView.tableHeaderView.setNeedsLayout()
        this.dataView.tableView.setNeedsLayout()
    }

    // "Ettevõte" is appended after "Teatis", so its weight is appended here too.
    _currentColumnWeights(): number[] {
        const baseWeights = RHEventLogViewTokens.notificationsColumnWeights
        if (!this._showsCompanyColumn) {
            return [...baseWeights, 1]
        }
        return [...baseWeights, RHEventLogViewTokens.notificationsCompanyColumnWeight, 1]
    }

    // See MyOngoingProcurementsViewController._updateCompanyColumnVisibility — same pattern.
    _updateCompanyColumnVisibility(shows: boolean) {
        if (this._showsCompanyColumn === shows) {
            return
        }
        this._showsCompanyColumn = shows
        this.dataView.descriptors = this._buildDescriptors()
        this._applyTableColumnLayout()
    }

    // See MyOngoingProcurementsViewController._refreshCompanyScopeAffordances for the same pattern.
    async _refreshCompanyScopeAffordances() {
        await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
        if (!this._hasAppliedDefaultCompanyScope) {
            this._hasAppliedDefaultCompanyScope = YES
            this._isAllCompaniesScopeActive = RHCore.sharedInstance.companyAffiliationStore.defaultAllCompaniesScopeActive
            this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
        }
        const hasMultipleCompanies = RHCore.sharedInstance.companyAffiliationStore.hasMultipleCompanyAffiliations
        this.filterAllCompaniesButton.enabled = hasMultipleCompanies
        this._updateCompanyColumnVisibility(hasMultipleCompanies)
        this._refreshFilterChips()
        this.view.setNeedsLayout()
    }

    _updateUnseenBadge() {
        // Page-pill "N uut" eemaldatud lihtsustamise käigus — hoia peidetud (lugemata read jäävad rasvaseks).
        this.unseenBadgeLabel.hidden = true
        this.view.setNeedsLayout()
    }

    _layoutDataViewEmptyState() {
        this.dataView.layoutNoDataMessageIfPossible()
    }

    _scheduleEmptyStateLayout() {
        requestAnimationFrame(() => this._layoutDataViewEmptyState())
        setTimeout(() => this._layoutDataViewEmptyState(), 50)
        setTimeout(() => this._layoutDataViewEmptyState(), 200)
    }
    
    // ── Row activation & inbox seen state ─────────────────────────────────────

    /** Hover text for a row: link rows describe where they go; the rest just
     *  describe the mark-seen affordance. */
    _rowHoverTextForNotification(notification: NotificationRow): string {
        if (notification.procurementId) {
            if (notification.qaSubtab === "private") {
                return "Vaata hanke privaatset teabevahetust"
            }
            if (notification.procurementTab === "qa") {
                return "Vaata hanke teabevahetust"
            }
            return "Vaata hanget"
        }
        if (notification.companyTab) {
            if (notification.companyTab === "affiliation") {
                return "Vaata minu ettevõtteid"
            }
            return "Vaata liikmeid"
        }
        return "Märgi nähtuks"
    }

    /** Bold an unseen row's cell text. The weight MUST live inside the text
     *  content, not on the title's own style: RHTableRowView.set data forces a
     *  normal weight onto the cell title AFTER our updateCell runs, so any
     *  `.style.fontWeight` we set is clobbered. UITextView renders its text as
     *  HTML, so an inline-styled span survives (inner inline style wins). */
    _boldIfUnseen(text: string, isNew: boolean): string {
        if (!isNew) {
            return text
        }
        return `<span style="font-weight:${"600"}">${text}</span>`
    }

    /** A row was clicked: mark it seen on the inbox surface (clears its bold
     *  emphasis) and, if it is a link, open its target. */
    _didActivateRow(dataItem: NotificationRow) {
        this._markRowSeenLocally(dataItem)
        RHCore.sharedInstance.notificationStore.markInboxEntriesSeen([dataItem._id]).catch(error => {
            console.warn("NotificationsViewController: markInboxEntriesSeen failed", error)
        })
        this._navigateToRowTargetIfAny(dataItem)
    }

    /** Optimistically clears one row's unseen styling so it updates instantly —
     *  important for non-link rows that stay on this page after the click. */
    _markRowSeenLocally(dataItem: NotificationRow) {
        let didChange = NO
        for (const row of this._rows) {
            if (row._id === dataItem._id && row.isNew) {
                row.isNew = NO
                didChange = YES
            }
        }
        if (didChange) {
            this.dataView.data = [...this._rows]
        }
        this._updateMarkAllButtonState()
    }

    /** Link rows open their procurement / company target; non-link rows do nothing. */
    _navigateToRowTargetIfAny(dataItem: NotificationRow) {
        let returnRoute: string | undefined
        if (this._selectedNotification?._id === dataItem._id && this.detailsView.isMemberOfViewTree) {
            returnRoute = UIRoute.currentRoute.stringRepresentation
        }
        if (dataItem.companyTab) {
            const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
            if (dataItem.companyId) {
                const companyAffiliation = companyAffiliationStore.affiliations.find(affiliation =>
                    affiliation.company?._id?.toString() === dataItem.companyId
                )
                let canActivateCompany = NO
                if (
                    companyAffiliation &&
                    companyAffiliation.source !== "relationship" &&
                    companyAffiliation.membershipStatus === "active"
                ) {
                    canActivateCompany = dataItem.companyTab === "affiliation"
                    if (dataItem.companyTab === "administration") {
                        canActivateCompany = companyAffiliation.isAdministrator === YES
                    }
                }
                if (canActivateCompany) {
                    companyAffiliationStore.deactivateDefaultAllCompaniesScope()
                    companyAffiliationStore.activateCompany(dataItem.companyId)
                }
            }
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(LogViewController.routeComponentName)
                .routeWithComponent(CompanyViewController.routeComponentName, {
                    [CompanyViewController.ParameterIdentifierName.tab]: dataItem.companyTab,
                    [CompanyViewController.ParameterIdentifierName.companyId]: dataItem.companyId ?? "",
                    [CompanyViewController.ParameterIdentifierName.workflowId]: dataItem.workflowId ?? ""
                })
                .apply()
            return
        }

        if (!dataItem.procurementId) {
            return
        }

        const didActivateCompany = this
            ._activateCompanyForNotification(dataItem.routeCompanyId)

        let route = UIRoute.currentRoute
            .routeByRemovingComponentNamed(LogViewController.routeComponentName)
            .routeWithViewControllerComponent(
                ProcurementDetailsViewController,
                {
                    [ProcurementDetailsViewController.ParameterIdentifierName.id]: dataItem.procurementId,
                    [ProcurementDetailsViewController.ParameterIdentifierName.backTo]: LogViewController.routeComponentName
                }
            )

        if (dataItem.procurementTab) {
            route = route.routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.tab,
                dataItem.procurementTab
            )
        }

        if (returnRoute) {
            route = route.routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.returnRoute, btoa(returnRoute)
            )
        }

        if (dataItem.questionId) {
            route = route.routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.questionId,
                dataItem.questionId
            )
        }

        if (dataItem.qaSubtab) {
            route = route.routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.qaSubtab,
                dataItem.qaSubtab
            )
        }

        if (dataItem.privateMessageCompanyId) {
            route = route.routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.privateMessageCompanyId,
                dataItem.privateMessageCompanyId
            )
        }

        if (
            didActivateCompany &&
            dataItem.routeApplicationMode &&
            RHCore.sharedInstance._applicationModeIsAvailable(dataItem.routeApplicationMode)
        ) {
            route = route.routeBySettingParameterInComponent(
                "settings",
                "mode",
                dataItem.routeApplicationMode
            )
        }

        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (activeCompanyId && (!dataItem.routeCompanyId || activeCompanyId === dataItem.routeCompanyId)) {
            RHCore.sharedInstance.notificationStore.beginTrackingReadingOfNotification({
                notificationId: dataItem._id, procurementId: dataItem.procurementId, companyId: activeCompanyId,
                applicationMode: dataItem.routeApplicationMode ?? this._currentApplicationMode,
                eventType: dataItem.eventType, tab: dataItem.procurementTab ?? "details",
                creationDate: dataItem.timestamp, contentId: dataItem.contentId
            })
        }
        route.apply()
    }


    _activateCompanyForNotification(companyId?: string): boolean {
        if (!companyId) {
            return NO
        }
        const affiliationStore = RHCore.sharedInstance.companyAffiliationStore
        const didActivateCompany = affiliationStore.activateCompany(companyId)
        if (!didActivateCompany) {
            return NO
        }
        affiliationStore.deactivateDefaultAllCompaniesScope()
        return YES
    }

    /** "Märgi kõik teavitused nähtuks": clear every unseen row's styling
     *  optimistically, then persist the shared event state + refresh unread emphasis. */
    _didPressMarkAllSeen() {
        let didChange = NO
        for (const row of this._rows) {
            if (row.isNew) {
                row.isNew = NO
                didChange = YES
            }
        }
        if (didChange) {
            this.dataView.data = [...this._rows]
        }
        this._updateMarkAllButtonState()
        RHCore.sharedInstance.notificationStore.markAllInboxNotificationsSeen().catch(error => {
            console.warn("NotificationsViewController: markAllInboxNotificationsSeen failed", error)
        })
    }

    /** Enable "Märgi nähtuks" only while there is at least one unseen row to mark;
     *  disable it on an empty inbox or once everything has been marked seen. */
    _updateMarkAllButtonState() {
        let hasUnseen = NO
        for (const row of this._rows) {
            if (row.isNew) {
                hasUnseen = YES
            }
        }
        this.markAllSeenButton.enabled = hasUnseen
    }
    
    // ── Data loading ──────────────────────────────────────────────────────────
    
    _requestedCompanyIdForScope(): string | undefined {
        if (this._isAllCompaniesScopeActive) {
            return undefined
        }
        return RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
    }

    _refreshFilterChips() {
        this.filterChipBar.chips = [this.filterAllButton, this.filterIncomingButton, this.filterOutgoingButton,
            this.moreFiltersButton, this.filterAllCompaniesButton]
        this.filterAllButton.chipActive = !this._direction && Object.keys(this._filterCriteria).length === 0
        this.filterIncomingButton.chipActive = this._direction === "incoming"
        this.filterOutgoingButton.chipActive = this._direction === "outgoing"
        this.moreFiltersButton.chipActive = Object.keys(this._filterCriteria).length > 0
        this.view.setNeedsLayout()
    }

    _filterTextForKey(key: string): string | undefined {
        const criterion = this._filterCriteria[key]
        if (criterion && "text" in criterion) return criterion.text
        return undefined
    }

    closeNotificationDetails() {
        this._selectedNotification = undefined
        this._detailsLoadGeneration++
        this.dataView.loading = NO
        this.loadFailureLabel.removeFromSuperview()
        this.detailsView.removeFromSuperview()
        this.detailsVisibilityDidChange?.(NO)
        for (const view of [this.titleLabel, this.markAllSeenButton, this.filterChipBar,
            this.dataView, this.previousPageButton, this.nextPageButton, this.pageLabel]) view.addedAsSubviewToView(this.view)
        if (this.filterPanel.isExpanded) this.filterPanel.addedAsSubviewToView(this.view)
        this.view.setNeedsLayoutUpToRootView()
    }

    async _showDetailsForNotification(notification: NotificationRow) {
        const generation = ++this._detailsLoadGeneration
        const core = RHCore.sharedInstance
        const activeCompanyId = core.companyAffiliationStore.activeCompanyId
        const applicationMode = core.applicationMode
        const userId = core.userProfile?._id?.toString()
        const companyId = notification.routeCompanyId ?? notification.companyId ?? activeCompanyId
        if (!companyId) return
        const requestedApplicationMode = notification.routeApplicationMode ?? this._currentApplicationMode
        const notificationReference = { notificationId: notification._id, companyId, applicationMode: requestedApplicationMode }
        const isDetailsLoadStillCurrent = () => generation === this._detailsLoadGeneration &&
            core.companyAffiliationStore.activeCompanyId === activeCompanyId && core.applicationMode === applicationMode &&
            core.userProfile?._id?.toString() === userId
        this.dataView.loading = YES
        this.loadFailureLabel.removeFromSuperview()
        try {
            const details = (await SocketClient.RetrieveNotificationDetails(notificationReference)).result
            if (!isDetailsLoadStillCurrent() || !this.view.isMemberOfViewTree) return
            this._selectedNotification = notification
            this.detailsView.applyDetailsWithFallbackSummaryHTML(details, notification.eventDescription)
            for (const view of [this.titleLabel, this.markAllSeenButton, this.filterChipBar, this.filterPanel,
                this.dataView, this.previousPageButton, this.nextPageButton, this.pageLabel]) view.removeFromSuperview()
            this.detailsView.addedAsSubviewToView(this.view)
            this.detailsVisibilityDidChange?.(YES)
            this.view.setNeedsLayoutUpToRootView()
            UIRoute.currentRoute.routeWithViewControllerComponent(LogViewController, {
                notificationId: notification._id, notificationCompanyId: companyId, notificationApplicationMode: requestedApplicationMode
            }, YES).applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
            requestAnimationFrame(() => {
                if (!isDetailsLoadStillCurrent() || !this.detailsView.isMemberOfViewTree) return
                void SocketClient.MarkNotificationAsRead(notificationReference).then(async () => {
                    if (!isDetailsLoadStillCurrent()) return
                    this._markRowSeenLocally(notification)
                    await core.notificationStore.markInboxEntriesSeen([notification._id])
                    const refreshedDetails = (await SocketClient.RetrieveNotificationDetails(notificationReference)).result
                    if (isDetailsLoadStillCurrent() && this.detailsView.isMemberOfViewTree) this.detailsView.applyDetailsWithFallbackSummaryHTML(refreshedDetails, notification.eventDescription)
                }).catch(error => console.error("Notification could not be marked as read", error))
            })
        } catch (error) {
            if (isDetailsLoadStillCurrent()) {
                this.loadFailureLabel.text = "Teatise detaile ei õnnestunud laadida. Proovi uuesti."
                this.loadFailureLabel.addedAsSubviewToView(this.view)
                this.view.setNeedsLayout()
                console.error("Notification details could not be loaded", error)
            }
        } finally {
            if (isDetailsLoadStillCurrent()) this.dataView.loading = NO
        }
    }

    async _loadNotifications() {
        const loadGeneration = ++this._loadGeneration
        this.dataView.loading = YES
        this.previousPageButton.enabled = NO
        this.nextPageButton.enabled = NO
        
        try {
            const result = (await SocketClient.RetrieveMyNotifications({
                limit: this._pageSize,
                companyId: this._requestedCompanyIdForScope(),
                includeProcurementHistory: YES,
                direction: this._direction,
                applicationMode: this._currentApplicationMode,
                referenceNumber: this._filterTextForKey("referenceNumber"),
                procurementTitle: this._filterTextForKey("title"),
                recipientSearchText: this._filterTextForKey("recipient"),
                page: this._pageNumber
            })).result
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            this._rows = result.logs.map(flattenEntry)
            this._unseenCount = result.unseenCount
            this._notificationCount = result.total
            this.pageLabel.text = `Lehekülg ${this._pageNumber} · Teatisi ${this._notificationCount}`
            this.previousPageButton.enabled = this._pageNumber > 1
            this.nextPageButton.enabled = this._pageNumber * this._pageSize < this._notificationCount
            this.dataView.noDataMessage = "Teated puuduvad"
            this._updateUnseenBadge()
            this.dataView.data = [...this._rows]
            this._updateMarkAllButtonState()
            this._scheduleEmptyStateLayout()
        }
        catch (error) {
            if (loadGeneration === this._loadGeneration) {
                this._rows = []
                this._notificationCount = 0
                this.dataView.data = []
                this.pageLabel.text = ""
                this._updateMarkAllButtonState()
                this.dataView.noDataMessage = "Teatisi ei õnnestunud laadida. Proovi uuesti."
                console.error("NotificationsViewController: failed to load notifications", error)
            }
        }
        finally {
            if (loadGeneration === this._loadGeneration) {
                this.dataView.loading = NO
                this._scheduleEmptyStateLayout()
            }
        }
    }
    
    // ── Lifecycle ─────────────────────────────────────────────────────────────

    /**
     * Public reload entry point. The parent LogViewController calls this when the
     * user returns to this page via SPA navigation — in that case the cached child
     * VC's viewDidAppear does NOT re-fire, so without this the "new" row dots would
     * keep showing the stale state from the previous visit (only a hard refresh
     * cleared them).
     */
    async reloadNotifications() {
        await this._refreshCompanyScopeAffordances()
        await this._loadNotifications()
        await this._restoreNotificationDetailsFromRoute(UIRoute.currentRoute)
    }

    override async viewDidAppear() {
        await super.viewDidAppear()
        await this.reloadNotifications()
    }

    async _restoreNotificationDetailsFromRoute(route: UIRoute): Promise<void> {
        const parameters = route.componentWithViewController(LogViewController)?.parameters
        const notificationId = parameters?.notificationId
        const companyId = parameters?.notificationCompanyId
        const applicationMode = parameters?.notificationApplicationMode
        if (!notificationId || !companyId || (applicationMode !== "procurer" && applicationMode !== "offerer")) return
        if (this._selectedNotification?._id === notificationId && this.detailsView.isMemberOfViewTree) return
        const generation = ++this._detailsLoadGeneration
        const core = RHCore.sharedInstance
        const activeCompanyId = core.companyAffiliationStore.activeCompanyId
        const activeApplicationMode = core.applicationMode
        const userId = core.userProfile?._id?.toString()
        const isDetailsRestorationStillCurrent = () => generation === this._detailsLoadGeneration && this.view.isMemberOfViewTree &&
            route.stringRepresentation === UIRoute.currentRoute.stringRepresentation &&
            activeCompanyId === core.companyAffiliationStore.activeCompanyId && activeApplicationMode === core.applicationMode &&
            userId === core.userProfile?._id?.toString()
        try {
            const result = (await SocketClient.RetrieveMyNotifications({
                notificationId, companyId, applicationMode, includeProcurementHistory: YES, limit: 1
            })).result
            if (!isDetailsRestorationStillCurrent()) return
            if (!result.logs[0]) throw new Error("Teatis pole kättesaadav.")
            await this._showDetailsForNotification(flattenEntry(result.logs[0]))
        } catch (error) {
            if (!isDetailsRestorationStillCurrent()) return
            this.loadFailureLabel.text = "Teatise detaile ei õnnestunud laadida. Proovi uuesti."
            this.loadFailureLabel.addedAsSubviewToView(this.view)
            this.view.setNeedsLayout()
            console.error("Notification details could not be restored", error)
        }
    }

    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
            event.name === RHCore.broadcastEventName.applicationModeDidChange ||
            event.name === RHCore.broadcastEventName.companyAffiliationsDidChange) {
            this.closeNotificationDetails()
            this._refreshFilterChips()
        }
        if (event.name === RHCore.broadcastEventName.companyAffiliationsDidChange) {
            this._refreshCompanyScopeAffordances()
        }
        if (
            event.name === RHCore.broadcastEventName.applicationModeDidChange ||
            event.name === RHCore.broadcastEventName.companyAffiliationsDidChange ||
            (event.name === RHCore.broadcastEventName.activeCompanyDidChange && this._isAllCompaniesScopeActive === NO)
        ) {
            this._pageNumber = 1
            this._rows.splice(0)
            this._unseenCount = 0
            this.dataView.data = []
            this._updateUnseenBadge()
            this._updateMarkAllButtonState()
            void this._loadNotifications()
        }
    }

    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        const referenceNumber = route.componentWithViewController(LogViewController)
            ?.parameters[LogViewController.ParameterIdentifierName.referenceNumber] ?? ""
        if (referenceNumber !== (this._filterTextForKey("referenceNumber") ?? "")) {
            this.filterPanel.applyTextCriterionForKey({ text: referenceNumber }, "referenceNumber")
            this._filterCriteria = this.filterPanel.criteria
            this._pageNumber = 1
            if (referenceNumber) this.filterPanel.isExpanded = YES
            this._refreshFilterChips()
        }
    }

    get _currentApplicationMode(): "procurer" | "offerer" {
        if (RHCore.sharedInstance.applicationMode === "procurer") return "procurer"
        return "offerer"
    }

    override async viewWillDisappear() {
        await super.viewWillDisappear()
        this._loadGeneration++
        this.closeNotificationDetails()
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const badgeHeight = RHBadges.notificationPageHeight
        const badgePaddingH = RHBadges.notificationPagePaddingX
        const bounds = this.view.contentBoundsWithInset(padding)

        // "Märgi kõik nähtuks" — right-aligned on the title row, compact height
        // vertically centred. Width from the label's own measurement plus padding
        // + a small buffer (the proven RHFeedbackViewTokens button-sizing pattern;
        // measure the title label, not the button — the button's own intrinsic
        // width is unreliable here).
        const markAllButtonWidth = [
            this.markAllSeenButton.titleLabel.intrinsicContentWidth(RHEventLogViewTokens.markAllButtonHeight)
            + RHSpacing.medium * 2
            + RHSpacing.small,
            (RHSpacing.xxLarge * 2)
        ].max()

        bounds
            .IF(this.detailsView.superview === this.view)
            .rectangleWithHeight(this.detailsView).assignedAsFrameOfView(this.detailsView)
            .ELSE()
            .rectangleWithHeight(labelHeight).assignedAsFrameOfView(this.titleLabel)
            .performingFunctionWithSelf(titleFrame => {
                titleFrame.rectangleWithWidth(markAllButtonWidth, 1)
                    .rectangleWithHeight(RHEventLogViewTokens.markAllButtonHeight, 0.5)
                    .assignedAsFrameOfView(this.markAllSeenButton)
                titleFrame.IF(!this.unseenBadgeLabel.hidden)
                    .rectangleWithWidth(this.unseenBadgeLabel.intrinsicContentWidth(badgeHeight) + badgePaddingH * 2, 1)
                    .rectangleWithHeight(badgeHeight, 0.5)
                    .assignedAsFrameOfView(this.unseenBadgeLabel)
                    .performingFunctionWithSelf(() => this.unseenBadgeLabel.setBorder(badgeHeight / 2, 0))
                    .ENDIF()
            })
            .rectangleForNextRow(padding, this.filterChipBar)
            .assignedAsFrameOfView(this.filterChipBar)
            .IF(this.filterPanel.superview === this.view)
            .rectangleForNextRow(padding, this.filterPanel)
            .assignedAsFrameOfView(this.filterPanel)
            .ENDIF()
            .IF(this.loadFailureLabel.superview === this.view)
            .rectangleForNextRow(padding, this.loadFailureLabel)
            .assignedAsFrameOfView(this.loadFailureLabel)
            .ENDIF()
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.dataView)
            .rectangleForNextRow(padding, padding * 2.5)
            .distributeViewsAlongWidth([this.previousPageButton, this.pageLabel, this.nextPageButton], [1, 3, 1], padding)
            .performingFunctionWithSelf(() => this._scheduleEmptyStateLayout())
            .ENDIF()
    }


    // ── Notification inbox routing (consultations, part cancellations, private messages) ──

    static _inboxDescriptionHTMLForEntry(entry: AuditLogEntry): string | undefined {
        let procurementTitle: string | undefined
        if (typeof entry.eventData.procurementTitle === "string") {
            procurementTitle = entry.eventData.procurementTitle
        }
        const procurementName = NotificationsViewController
            ._inboxProcurementNameHTML(procurementTitle)

        if (entry.eventType === "procurement.responsible_changed") {
            return `Sind määrati hanke${procurementName} vastutavaks isikuks.`
        }
        if (entry.eventType === "procurement.opening_protocol_shared") {
            return `Hanke${procurementName} pakkumuste avamise protokoll on nüüd allalaetav.`
        }
        if (entry.eventType === "procurement.reconciliation_invitation_sent") {
            return `Sind kutsuti esitama pakkumust täpsustavas voorus${procurementName}.`
        }
        if (entry.eventType === "procurement.consultation_created") {
            let targetTitle: string | undefined
            if (typeof entry.eventData.targetTitle === "string") {
                targetTitle = entry.eventData.targetTitle
            }
            const targetDescription = NotificationsViewController
                ._inboxConsultationTargetDescriptionHTML(targetTitle)
            if (procurementName) {
                return `Sinult paluti konsultatsiooni hankes${procurementName}${targetDescription}.`
            }
            return `Sinult paluti hanke kohta konsultatsiooni${targetDescription}.`
        }
        if (entry.eventType === "procurement.consultation_commented") {
            let targetTitle: string | undefined
            if (typeof entry.eventData.targetTitle === "string") {
                targetTitle = entry.eventData.targetTitle
            }
            const targetDescription = NotificationsViewController
                ._inboxConsultationTargetDescriptionHTML(targetTitle)
            return `Hanke${procurementName} konsultatsioonile${targetDescription} lisati kommentaar.`
        }
        if (entry.eventType === "offer.responsible_changed") {
            return `Sind määrati hanke${procurementName} pakkumuse eest vastutavaks isikuks.`
        }
        if (entry.eventType === "offer.required_documents_changed") {
            if (Array.isArray(entry.eventData.partNumbers)) {
                const partNumbers = entry.eventData.partNumbers.filter(partNumber =>
                    typeof partNumber === "number" && Number.isInteger(partNumber) && partNumber > 0
                )
                if (partNumbers.length === 1) {
                    return `Hanke${procurementName} osa ${partNumbers[0]} pakkumus ei vasta uutele ` +
                        "kohustuslike dokumentide nõuetele. Palun esita selle osa pakkumus uuesti."
                }
                if (partNumbers.length > 1) {
                    return `Hanke${procurementName} osade ${partNumbers.join(", ")} pakkumused ei vasta uutele ` +
                        "kohustuslike dokumentide nõuetele. Palun esita nende osade pakkumused uuesti."
                }
            }
            return `Hanke${procurementName} pakkumus ei vasta uutele kohustuslike dokumentide nõuetele. Palun esita pakkumus uuesti.`
        }
        if (entry.eventType === "offer.part_closed_without_contract") {
            const data = entry.eventData
            let partNumber: number | undefined
            let numberOfParts: number | undefined
            let reason: string | undefined
            if (typeof data.partNumber === "number") {
                partNumber = data.partNumber
            }
            if (typeof data.partCount === "number") {
                numberOfParts = data.partCount
            }
            if (typeof data.reason === "string") {
                reason = data.reason
            }
            return NotificationsViewController._inboxPartCancellationDescriptionHTML(
                procurementName,
                partNumber,
                numberOfParts,
                reason
            )
        }
        if (entry.eventType === "privateMessage.sent") {
            let senderRole: RHProcurementPrivateMessageSenderRole | undefined
            if (
                entry.eventData.senderRole === "offerer" ||
                entry.eventData.senderRole === "procurer"
            ) {
                senderRole = entry.eventData.senderRole
            }
            if (!senderRole) {
                return undefined
            }
            let senderCompanyName: string | undefined
            if (
                typeof entry.eventData.companyName === "string" &&
                entry.eventData.companyName.trim()
            ) {
                senderCompanyName = entry.eventData.companyName.trim()
            }
            return NotificationsViewController._inboxPrivateMessageDescriptionHTML(
                procurementName,
                senderRole,
                senderCompanyName
            )
        }
        return undefined
    }


    static _inboxTargetForEventType(eventType: string): NotificationInboxTarget | undefined {
        if (eventType === "procurement.reconciliation_invitation_sent") {
            return { procurementTab: "makeOffer" }
        }
        if (eventType === "procurement.opening_protocol_shared") {
            return { procurementTab: "details" }
        }
        if (eventType === "procurement.responsible_changed") {
            return { procurementTab: "team" }
        }
        if (
            eventType === "procurement.consultation_created" ||
            eventType === "procurement.consultation_commented"
        ) {
            return { procurementTab: "details" }
        }
        if (eventType === "offer.responsible_changed") {
            return { procurementTab: "team" }
        }
        if (eventType === "offer.part_closed_without_contract" || eventType === "offer.required_documents_changed") {
            return { procurementTab: "makeOffer" }
        }
        if (eventType === "privateMessage.sent") {
            return { procurementTab: "qa", qaSubtab: "private" }
        }
        return undefined
    }


    static _inboxRecipientCompanyIdForEntry(entry: AuditLogEntry): string | undefined {
        if (entry.notificationCompany?._id) {
            return entry.notificationCompany._id
        }
        if (typeof entry.eventData.recipientCompanyId === "string") {
            return entry.eventData.recipientCompanyId
        }
        return undefined
    }


    static _inboxApplicationModeForEntry(
        entry: AuditLogEntry
    ): "procurer" | "offerer" | undefined {
        if (entry.eventType === "offer.evaluation_reopened") {
            if (NotificationsViewController._inboxRecipientCompanyIdForEntry(entry) === entry.eventData.procuringCompanyId) {
                return "procurer"
            }
            return "offerer"
        }
        if (
            entry.eventType === "procurement.published" ||
            entry.eventType === "procurement.deadline_passed" ||
            entry.eventType === "procurement.team_member_added" ||
            entry.eventType === "procurement.team_member_role_changed" ||
            entry.eventType === "procurement.responsible_changed" ||
            entry.eventType === "procurement.consultation_created" ||
            entry.eventType === "procurement.consultation_commented" ||
            entry.eventType === "offer.submitted" ||
            entry.eventType === "offer.part_modified" ||
            entry.eventType === "offer.rescinded" ||
            entry.eventType === "offer.part_rescinded" ||
            entry.eventType === "question.asked"
        ) {
            return "procurer"
        }
        if (
            (entry.eventType === "contract.cancelled" ||
                entry.eventType === "procurement.deadline_auto_extended") &&
            entry.actor.companyId &&
            NotificationsViewController._inboxRecipientCompanyIdForEntry(entry) === entry.actor.companyId
        ) {
            return "procurer"
        }
        if (
            entry.eventType === "procurement.amended" ||
            entry.eventType === "procurement.deadline_auto_extended" ||
            entry.eventType === "procurement.opening_protocol_shared" ||
            entry.eventType === "procurement.reconciliation_invitation_sent" ||
            entry.eventType === "procurement.closed_without_contract" ||
            entry.eventType === "offer.opened" ||
            entry.eventType === "offer.required_documents_changed" ||
            entry.eventType === "offer.responsible_changed" ||
            entry.eventType === "offer.part_closed_without_contract" ||
            entry.eventType === "offer.notification_sent" ||
            entry.eventType === "contract.signed" ||
            entry.eventType === "contract.cancelled" ||
            entry.eventType === "question.answered" ||
            entry.eventType === "procurerBroadcast.sent"
        ) {
            return "offerer"
        }
        if (entry.eventData.senderRole === "offerer") {
            return "procurer"
        }
        if (entry.eventData.senderRole === "procurer") {
            return "offerer"
        }
        return undefined
    }


    static _inboxProcurementNameHTML(procurementTitle?: string): string {
        if (!procurementTitle?.trim()) {
            return ""
        }
        return ` „${RHTextSanitizer.escapedHTML(procurementTitle.trim())}“`
    }


    static _inboxConsultationTargetDescriptionHTML(targetTitle?: string): string {
        if (!targetTitle?.trim()) {
            return ""
        }
        return ` teemal „${RHTextSanitizer.escapedHTML(targetTitle.trim())}“`
    }


    static _inboxPartCancellationDescriptionHTML(
        procurementName: string,
        partNumber?: number,
        numberOfParts?: number,
        reason?: string
    ): string {
        let description = `Hanke${procurementName} osa lõpetati ilma lepinguta.`
        if (
            numberOfParts !== undefined &&
            Number.isInteger(numberOfParts) &&
            numberOfParts > 1 &&
            partNumber !== undefined &&
            Number.isInteger(partNumber) &&
            partNumber > 0
        ) {
            description = `Hanke${procurementName} osa ${partNumber} lõpetati ilma lepinguta.`
        }
        if (reason?.trim()) {
            description += ` Põhjus: ${RHTextSanitizer.escapedHTML(reason.trim())}`
        }
        return description
    }


    static _inboxPrivateMessageDescriptionHTML(
        procurementName: string,
        senderRole: RHProcurementPrivateMessageSenderRole,
        senderCompanyName?: string
    ): string {
        if (!procurementName) {
            if (senderRole === "procurer") {
                if (senderCompanyName?.trim()) {
                    return `Hankija ${RHTextSanitizer.escapedHTML(senderCompanyName.trim())} ` +
                        "saatis privaatse sõnumi."
                }
                return "Hankija saatis privaatse sõnumi."
            }
            if (senderCompanyName?.trim()) {
                return `Pakkuja ${RHTextSanitizer.escapedHTML(senderCompanyName.trim())} ` +
                    "saatis privaatse sõnumi."
            }
            return "Pakkuja saatis privaatse sõnumi."
        }
        if (senderRole === "procurer") {
            if (senderCompanyName?.trim()) {
                return `Hankija ${RHTextSanitizer.escapedHTML(senderCompanyName.trim())} ` +
                    `saatis privaatse sõnumi hankes${procurementName}.`
            }
            return `Hankija saatis privaatse sõnumi hankes${procurementName}.`
        }
        if (senderCompanyName?.trim()) {
            return `Pakkuja ${RHTextSanitizer.escapedHTML(senderCompanyName.trim())} ` +
                `saatis privaatse sõnumi hankes${procurementName}.`
        }
        return `Pakkuja saatis privaatse sõnumi hankes${procurementName}.`
    }


    // ── Procurement amendment notifications ───────────────────────────────────

    static _amendmentProcurementTab(eventType: string): "qa" | undefined {
        if (eventType === "procurement.amended") {
            return "qa"
        }
        return undefined
    }


    // ── Incoming offer notifications ─────────────────────────────────────────

    static _incomingOfferProcurementTab(
        eventType: string
    ): "offers" | undefined {
        if (
            eventType === "offer.submitted" ||
            eventType === "offer.part_modified"
        ) {
            return "offers"
        }
        return undefined
    }


    static _incomingOfferDescriptionHTML(
        eventType: string,
        procurementTitle: string | undefined,
        partNumber?: number,
        offererCompanyName?: string
    ): string | undefined {
        let escapedOffererCompanyName: string | undefined
        if (offererCompanyName?.trim()) {
            escapedOffererCompanyName = RHTextSanitizer.escapedHTML(offererCompanyName.trim())
        }
        let description: string | undefined
        if (eventType === "offer.submitted") {
            description = "Esitati pakkumus"
            if (escapedOffererCompanyName) {
                description = `Pakkuja ${escapedOffererCompanyName} esitas pakkumuse`
            }
        }
        else if (eventType === "offer.part_modified") {
            description = "Pakkumust muudeti"
            if (escapedOffererCompanyName) {
                description = `Pakkuja ${escapedOffererCompanyName} muutis pakkumust`
            }
        }
        else if ((eventType === "offer.rescinded" || eventType === "offer.part_rescinded") &&
            escapedOffererCompanyName) {
            description = `Pakkuja ${escapedOffererCompanyName} võttis pakkumuse tagasi`
        }
        if (!description) {
            return undefined
        }

        if (typeof procurementTitle === "string" && procurementTitle) {
            description += ` hankes „${RHTextSanitizer.escapedHTML(procurementTitle)}“`
        }
        if ((eventType === "offer.part_modified" || eventType === "offer.part_rescinded") &&
            partNumber !== undefined && Number.isInteger(partNumber) && partNumber > 0) {
            description += `, osa ${partNumber}`
        }
        return description
    }


    // ── Company membership notifications ─────────────────────────────────────

    static _companyMembershipDescriptionHTML(
        eventType: string,
        companyName: string | undefined,
        eventData: Record<string, unknown>
    ): string | undefined {
        let escapedCompanyName: string | undefined
        if (typeof companyName === "string" && companyName.trim()) {
            escapedCompanyName = RHTextSanitizer.escapedHTML(companyName.trim())
        }

        let escapedUserDisplayName: string | undefined
        if (typeof eventData.userDisplayName === "string" && eventData.userDisplayName.trim()) {
            escapedUserDisplayName = RHTextSanitizer.escapedHTML(eventData.userDisplayName.trim())
        }

        if (eventType === "company.join_request_created") {
            if (escapedUserDisplayName && escapedCompanyName) {
                return `Kasutaja ${escapedUserDisplayName} taotles liitumist ` +
                    `ettevõttega „${escapedCompanyName}“.`
            }
            if (escapedCompanyName) {
                return `Kasutaja taotles liitumist ettevõttega „${escapedCompanyName}“.`
            }
            return "Kasutaja esitas ettevõttega liitumise taotluse."
        }
        if (eventType === "company.join_request_approved") {
            if (escapedCompanyName) {
                return `Sinu taotlus ettevõttega „${escapedCompanyName}“ liitumiseks kinnitati.`
            }
            return "Sinu ettevõttega liitumise taotlus kinnitati."
        }
        if (eventType === "company.user_removed") {
            if (escapedCompanyName) {
                return `Sind eemaldati ettevõttest „${escapedCompanyName}“.`
            }
            return "Sind eemaldati ettevõttest."
        }
        if (eventType === "company.join_request_rejected") {
            if (escapedCompanyName) {
                return `Sinu taotlus ettevõttega „${escapedCompanyName}“ liitumiseks lükati tagasi.`
            }
            return "Sinu ettevõttega liitumise taotlus lükati tagasi."
        }
        if (eventType === "company.user_invited") {
            if (escapedCompanyName) {
                return `Ettevõtte „${escapedCompanyName}“ peakasutaja kutsus sind ` +
                    "selle ettevõtte kasutajaks."
            }
            return "Ettevõtte peakasutaja kutsus sind selle ettevõtte kasutajaks."
        }
        if (eventType === "company.invitation_accepted") {
            if (escapedUserDisplayName && escapedCompanyName) {
                return `Kasutaja ${escapedUserDisplayName} võttis vastu kutse ` +
                    `ettevõttesse „${escapedCompanyName}“.`
            }
            if (escapedCompanyName) {
                return `Kutsutud kasutaja võttis vastu kutse ettevõttesse „${escapedCompanyName}“.`
            }
            return "Kutsutud kasutaja võttis ettevõtte kutse vastu."
        }
        if (eventType === "company.invitation_rejected") {
            if (escapedUserDisplayName && escapedCompanyName) {
                return `Kasutaja ${escapedUserDisplayName} lükkas tagasi kutse ` +
                    `ettevõttesse „${escapedCompanyName}“.`
            }
            if (escapedCompanyName) {
                return `Kutsutud kasutaja lükkas tagasi kutse ettevõttesse „${escapedCompanyName}“.`
            }
            return "Kutsutud kasutaja lükkas ettevõtte kutse tagasi."
        }
        if (eventType === "company.invitation_revoked") {
            if (escapedCompanyName) {
                return `Sulle saadetud kutse ettevõttesse „${escapedCompanyName}“ tühistati.`
            }
            return "Sulle saadetud ettevõtte kutse tühistati."
        }
        if (eventType === "company.join_request_cancelled") {
            if (escapedUserDisplayName && escapedCompanyName) {
                return `Kasutaja ${escapedUserDisplayName} tühistas taotluse liituda ` +
                    `ettevõttega „${escapedCompanyName}“.`
            }
            if (escapedCompanyName) {
                return `Kasutaja tühistas taotluse liituda ettevõttega „${escapedCompanyName}“.`
            }
            return "Kasutaja tühistas ettevõttega liitumise taotluse."
        }
        if (eventType === "company.membership_workflow_expired") {
            if (eventData.direction === "user_to_company") {
                if (escapedCompanyName) {
                    return `Sinu taotlus liituda ettevõttega „${escapedCompanyName}“ aegus.`
                }
                return "Sinu ettevõttega liitumise taotlus aegus."
            }
            if (eventData.direction === "company_to_user") {
                if (escapedUserDisplayName && escapedCompanyName) {
                    return `Kasutajale ${escapedUserDisplayName} saadetud kutse ` +
                        `ettevõttesse „${escapedCompanyName}“ aegus.`
                }
                if (escapedCompanyName) {
                    return `Kasutajale saadetud kutse ettevõttesse „${escapedCompanyName}“ aegus.`
                }
                return "Kasutajale saadetud ettevõtte kutse aegus."
            }
            return "Ettevõtte liikmesusega seotud toimingu tähtaeg möödus."
        }
        if (eventType === "company.user_role_changed") {
            if (escapedCompanyName) {
                return `Sinu rolle ettevõttes „${escapedCompanyName}“ muudeti.`
            }
            return "Sinu rolle ettevõttes muudeti."
        }
        return undefined
    }


    static _companyMembershipTabForEvent(
        eventType: string,
        eventData: Record<string, unknown>
    ): "administration" | "affiliation" | undefined {
        if (
            eventType === "company.join_request_created" ||
            eventType === "company.join_request_cancelled" ||
            eventType === "company.invitation_accepted" ||
            eventType === "company.invitation_rejected"
        ) {
            return "administration"
        }

        if (
            eventType === "company.user_invited" ||
            eventType === "company.join_request_approved" ||
            eventType === "company.join_request_rejected" ||
            eventType === "company.invitation_revoked"
        ) {
            return "affiliation"
        }

        if (eventType === "company.membership_workflow_expired") {
            if (eventData.direction === "user_to_company") {
                return "affiliation"
            }
            if (eventData.direction === "company_to_user") {
                return "administration"
            }
        }

        return undefined
    }


    // ── Contract notifications ────────────────────────────────────────────────

    static _contractProcurementTab(
        eventType: string
    ): "contracts" | undefined {
        if (
            eventType === "contract.signed" ||
            eventType === "contract.cancelled"
        ) {
            return "contracts"
        }
        return undefined
    }


    static _signedContractDescriptionHTML(
        procurementTitle: string | undefined,
        partNumber: number | undefined,
        partCount: number | undefined
    ): string {
        const procurementDescription =
            NotificationsViewController._contractProcurementDescriptionHTML(procurementTitle)

        if (
            NotificationsViewController._contractHasPartSpecificDescription(partNumber, partCount)
        ) {
            return `${procurementDescription} osa ${partNumber} kohta sõlmiti leping.`
        }

        return `${procurementDescription} on sõlmitud leping.`
    }


    static _cancelledContractDescriptionHTML(
        procurementTitle: string | undefined,
        partNumber: number | undefined,
        partCount: number | undefined,
        wasSignedBeforeCancellation: boolean,
        nonConclusionReason?: string
    ): string {
        let procurementPartDescription = "Hanke"
        if (typeof procurementTitle === "string" && procurementTitle) {
            procurementPartDescription += ` „${RHTextSanitizer.escapedHTML(procurementTitle)}“`
        }

        let cancellationReasonSuffix = ""
        if (typeof nonConclusionReason === "string" && nonConclusionReason) {
            cancellationReasonSuffix = " Põhjendus: " + RHTextSanitizer.escapedHTML(nonConclusionReason)
        }

        if (
            NotificationsViewController._contractHasPartSpecificDescription(partNumber, partCount)
        ) {
            procurementPartDescription += ` osa ${partNumber}`
        }
        if (wasSignedBeforeCancellation) {
            return `${procurementPartDescription} leping tühistati.${cancellationReasonSuffix}`
        }
        return `${procurementPartDescription} kohta lepingut ei sõlmitud.${cancellationReasonSuffix}`
    }


    static _contractProcurementDescriptionHTML(procurementTitle: string | undefined): string {
        let procurementDescription = "Hankes"
        if (typeof procurementTitle === "string" && procurementTitle) {
            procurementDescription +=
                ` „${RHTextSanitizer.escapedHTML(procurementTitle)}“`
        }
        return procurementDescription
    }


    static _contractHasPartSpecificDescription(
        partNumber: number | undefined,
        partCount: number | undefined
    ): boolean {
        return (
            typeof partCount === "number" &&
            Number.isInteger(partCount) &&
            partCount > 1 &&
            typeof partNumber === "number" &&
            Number.isInteger(partNumber) &&
            partNumber > 0
        )
    }


    // ── Procurement team notifications ────────────────────────────────────────

    static _procurementTeamDescriptionHTML(
        eventType: string,
        procurementTitle: string | undefined,
        role: string | undefined
    ): string | undefined {
        if (!NotificationsViewController._isSupportedProcurementTeamEventType(eventType)) {
            return undefined
        }

        let procurementName = ""
        if (typeof procurementTitle === "string" && procurementTitle.trim()) {
            procurementName = ` „${RHTextSanitizer.escapedHTML(procurementTitle.trim())}“`
        }

        if (eventType === "procurement.team_member_added") {
            const roleDescription =
                NotificationsViewController._addedTeamRoleDescription(role)
            return `Sind lisati hanke${procurementName} meeskonda${roleDescription}.`
        }

        const roleDescription = NotificationsViewController._changedTeamRoleDescription(role)
        return `Sinu rolli hanke${procurementName} meeskonnas muudeti${roleDescription}.`
    }


    static _procurementTeamTabForEventType(eventType: string): "team" | undefined {
        if (NotificationsViewController._isSupportedProcurementTeamEventType(eventType)) {
            return "team"
        }
        return undefined
    }


    static _isSupportedProcurementTeamEventType(
        eventType: string
    ): eventType is "procurement.team_member_added" | "procurement.team_member_role_changed" {
        return eventType === "procurement.team_member_added" ||
            eventType === "procurement.team_member_role_changed"
    }


    static _addedTeamRoleDescription(role: string | undefined): string {
        if (role === "responsible") { return " vastutava isikuna" }
        if (role === "authorized") { return " volitatud isikuna" }
        if (role === "observer") { return " vaatlejana" }
        if (role === "evaluator") { return " hindajana" }
        return ""
    }


    static _changedTeamRoleDescription(role: string | undefined): string {
        if (role === "responsible") { return " vastutavaks isikuks" }
        if (role === "authorized") { return " volitatud isikuks" }
        if (role === "observer") { return " vaatlejaks" }
        if (role === "evaluator") { return " hindajaks" }
        return ""
    }


    // ── Question & answer notifications ───────────────────────────────────────

    static _answeredQuestionDescriptionHTML(
        procurementTitle: string | undefined,
        isQuestionFromRecipientCompany: boolean
    ): string {
        if (typeof procurementTitle === "string" && procurementTitle) {
            const procurementName = ` „${RHTextSanitizer.escapedHTML(procurementTitle)}“`
            if (isQuestionFromRecipientCompany) {
                return `Sinu küsimusele hankes${procurementName} vastati`
            }
            return `Hankija vastas küsimusele hankes${procurementName}`
        }

        if (isQuestionFromRecipientCompany) {
            return "Sinu küsimusele vastati"
        }
        return "Hankija vastas küsimusele"
    }


    // ── Offer result notifications ────────────────────────────────────────────

    static _offerResultDescriptionHTML(
        procurementTitle: string | undefined,
        isWinner: boolean,
        formattedOfferPrice: string | undefined,
        partNumber: number | undefined,
        partCount: number | undefined,
        partHasWinner?: boolean,
        isReconciliationRound?: boolean
    ): string {
        let procurementName = ""
        if (typeof procurementTitle === "string" && procurementTitle) {
            procurementName = ` „${RHTextSanitizer.escapedHTML(procurementTitle)}“`
        }

        const hasPartSpecificIdentity =
            NotificationsViewController._offerResultHasPartSpecificIdentity(partNumber, partCount)

        let resultDescription = "Sinu pakkumust ei tunnistatud edukaks"
        if (procurementName) {
            resultDescription = `Sinu pakkumust ei tunnistatud hankes${procurementName} edukaks`
        }
        if (isWinner) {
            resultDescription = "Sinu pakkumus tunnistati edukaks"
            if (procurementName) {
                resultDescription = `Sinu pakkumus tunnistati hankes${procurementName} edukaks`
            }
        }
        else if (partHasWinner === NO) {
            if (isReconciliationRound === YES) {
                return `Täpsustavas voorus${procurementName} ei tunnistatud ühtegi pakkumust edukaks. ` +
                    "Selle vooru tulemusel lepingut ei sõlmita."
            }
            if (hasPartSpecificIdentity) {
                return `Hanke${procurementName} osas ${partNumber} ei tunnistatud ` +
                    "ühtegi pakkumust edukaks. Lepingut ei sõlmita."
            }
            if (procurementName) {
                return `Hankes${procurementName} ei tunnistatud ühtegi pakkumust edukaks. ` +
                    "Lepingut ei sõlmita."
            }
            return "Ühtegi pakkumust ei tunnistatud edukaks. Lepingut ei sõlmita."
        }

        if (typeof formattedOfferPrice !== "string" || !formattedOfferPrice.trim()) {
            if (hasPartSpecificIdentity) {
                return `${resultDescription} — osa ${partNumber}`
            }
            return resultDescription
        }

        const safeOfferPrice = RHTextSanitizer.escapedHTML(formattedOfferPrice.trim())
        let offerIdentity = `pakkumuse hind ${safeOfferPrice}`
        if (hasPartSpecificIdentity && partHasWinner !== NO) {
            offerIdentity = `osa ${partNumber}, ${offerIdentity}`
        }

        return `${resultDescription} — ${offerIdentity}`
    }


    static _offerResultHasPartSpecificIdentity(partNumber: number | undefined, partCount: number | undefined): boolean {
        return (
            typeof partCount === "number" &&
            Number.isInteger(partCount) &&
            partCount > 1 &&
            typeof partNumber === "number" &&
            Number.isInteger(partNumber) &&
            partNumber > 0
        )
    }

}

// ─────────────────────────────────────────────────────────────────────────────
// markProcurementNotificationsAsSeen
// Call from ProcurementDetailsViewController.viewDidAppear so that opening
// a procurement page also clears unseen notification badges for that procurement.
// Fire-and-forget.
// ─────────────────────────────────────────────────────────────────────────────

export function markProcurementNotificationsAsSeen(procurementId: string): void {
    RHCore.sharedInstance.notificationStore.markSeen(procurementId).catch(e => {
        console.warn("markProcurementNotificationsAsSeen: request failed", e)
    })
}
